import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Typography, Box } from '@mui/material';
import { hasValidAuthTokens, isTokenExpired } from 'src/utils/utils';
import { setToastMessage } from 'src/store/my_pages/app';
import { dispatch } from 'src/store/Store';
import { publicRoutes } from 'src/routes/Router';
import { attemptTokenRefresh, clearAllTokens, hasAnyTokens, getAuthTokens } from 'src/utils/tokenRefresh';

/* 
When it runs => Only when navigating between routes or on initial page load
Purpose => Determines if a user should be allowed to access a protected route
Scope => Route-level authentication check
Frequency => Only runs on route changes, not on every API call
*/

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if current route is a public route
        const currentPath = location.pathname;
        const isPublicRoute = publicRoutes.some((route) => route.path === currentPath);

        if (isPublicRoute) {
          setIsAuthenticated(true);
          setIsChecking(false);
          return;
        }

        // Quick validation check - if no valid tokens at all, redirect immediately
        if (!hasValidAuthTokens()) {
          navigate('/auth/login');
          dispatch(
            setToastMessage({
              msg: 'You are not logged in. Please login to continue',
              color: 'warning',
            }),
          );
          clearAllTokens();
          return;
        }

        // Get tokens for detailed checks
        const { accessToken, refreshToken, accessTokenExpiry, refreshTokenExpiry } = getAuthTokens();

        // Check if access token is expired or missing
        const isAccessTokenExpired = isTokenExpired(accessTokenExpiry);
        const hasValidRefreshToken = refreshToken && refreshTokenExpiry && !isTokenExpired(refreshTokenExpiry);

        // If access token is missing/expired but we have a valid refresh token, try to refresh
        if ((!accessToken || isAccessTokenExpired) && hasValidRefreshToken) {
          const refreshResult = await attemptTokenRefresh(refreshToken);

          if (refreshResult.success) {
            setIsAuthenticated(true);
            setIsChecking(false);
            return;
          } else {
            // Refresh failed
            clearAllTokens();
            navigate('/auth/login');
            dispatch(
              setToastMessage({
                msg: 'Session expired, please login again',
                iconKey: 'error',
                color: 'warning',
                duration: 5000,
              }),
            );
            return;
          }
        }

        // If we reach here, we have valid tokens
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Error checking authentication:', error);
        navigate('/auth/login');
        dispatch(
          setToastMessage({
            msg: 'Authentication error, please login again',
            color: 'error',
          }),
        );
      } finally {
        setIsChecking(false);
      }
    };

    checkAuthentication();
  }, [navigate, location.pathname]);

  // Show loading while checking authentication
  if (isChecking) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '2rem',
          height: '100vh',
        }}
      >
        <CircularProgress color="secondary" size={30} />
        <Typography>Checking authentication...</Typography>
      </Box>
    );
  }

  // If not authenticated, don't render children (redirect will happen)
  if (!isAuthenticated) {
    return null;
  }

  // If authenticated, render the protected content
  return <>{children}</>;
};

export default AuthGuard;
